import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requestEmailCode } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import LoginLanguageSwitcher from '../components/auth/LoginLanguageSwitcher';
import EmailCodeChallenge from '../components/auth/EmailCodeChallenge';
import { readCodeFlow, saveCodeFlow, codeError } from '../lib/emailCodeFlow';

export default function ForgotPassword() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const sv = (i18n.resolvedLanguage || i18n.language || 'sv').startsWith('sv');
  const [email, setEmail] = useState(typeof location.state?.email === 'string' ? location.state.email : '');
  const [loading, setLoading] = useState(false);
  const [flow, setFlow] = useState(() => readCodeFlow('reset'));
  const [errorMessage, setErrorMessage] = useState('');
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const identifier = email.trim();
    if (inFlight.current || !identifier) return;
    inFlight.current = true;
    setLoading(true);
    setErrorMessage('');
    try {
      const result = await requestEmailCode(identifier, 'reset');
      if (!mounted.current) return;
      setFlow(saveCodeFlow(result.data, identifier, 'reset'));
    } catch (error) {
      // The server's neutral 2xx response protects account privacy. A network
      // or service error is not a confirmation and must keep the form open.
      if (mounted.current) setErrorMessage(codeError(error, sv));
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-start justify-center py-8 px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[rgb(var(--tn-text-ffffff))] tracking-tight">
            {t('common:brand.name')}
          </h1>
          <p className="text-muted-foreground">{t('common:brand.tagline')}</p>
        </div>

        <Card className="bg-card border-border">
          {!flow && <CardHeader>
            <CardTitle className="font-heading text-2xl">{t('auth:forgot.title')}</CardTitle>
            <CardDescription>
              {sv ? 'Bekräfta ditt konto med en mejlkod och välj sedan ett nytt lösenord.' : 'Confirm your account with an email code, then choose a new password.'}
            </CardDescription>
          </CardHeader>}
          <CardContent className={flow ? "pt-6" : undefined}>
            {flow ? (
              <EmailCodeChallenge flow={flow} onChange={setFlow}
                onBack={() => { setEmail(flow.identifier); setFlow(null); }}
                onComplete={data => navigate('/reset-password', { replace: true, state: { resetToken: data.reset_token } })} />
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {errorMessage && <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm" data-testid="forgot-error">{errorMessage}</p>}
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth:forgot.email_label')}</Label>
                  <Input
                    id="email"
                    type="text"
                    placeholder={t('auth:forgot.email_placeholder')}
                    value={email}
                    disabled={loading}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="username"
                    data-testid="forgot-email-input"
                    className="bg-background border-border"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t('auth:forgot.email_hint')}
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={loading || !email.trim()}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {sv ? 'Skicka mejlkod' : 'Send email code'}
                </Button>
                <Link to="/login?mode=password" state={{ email: email.trim() }}>
                  <Button variant="ghost" className="w-full mt-2">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('auth:login.back_to_login')}
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <LoginLanguageSwitcher />
      </div>
    </div>
  );
}
